import { addMinutes, format } from 'date-fns';
import { dateFormat, datetimeFormat } from '../Constants/Date.constant';
import { documentActionNames, documentTypeNames } from '../Constants/DocumentNaming.constant';
import parsePhoneNumberFromString, { isValidPhoneNumber } from 'libphonenumber-js';

/**
 * Build URL query string from params object, filtering out null/empty values
 * @param {Object} params - Parameters to convert to query string
 * @returns {string} Query string (empty string if no params)
 */
export const buildQueryString = (params = {}) => {
    const queryParams = new URLSearchParams();

    Object.entries(params)
        .filter(([, value]) => value != null && value !== '')
        .forEach(([key, value]) => queryParams.set(key, value));

    return queryParams.toString();
};

export const getTitle = (type) => {
    return documentTypeNames[type] ?? type;
};

export const shortId = (longId, startCharsCount = 3, endCharsCount = 3, middleFiller = '..') => {
    if (longId.length <= startCharsCount + endCharsCount) {
        return longId;
    }

    const start = longId.slice(0, startCharsCount);
    const end = longId.slice(-endCharsCount);

    return `${start}${middleFiller}${end}`;
};

const simplifyDateRange = (dateRange) => {
    const dates = dateRange.split(' - ');
    const startDate = dates[0].split(' ')[0];
    const endDate = dates[1].split(' ')[0];
    return `${startDate} - ${endDate}`;
};

export const transformOverallStatisticsData = (apiResponse, isGroupedByOrg) => {
    const rows = apiResponse.rows;
    const types = ['documents', 'purchases', 'scheduleOrders', 'titleSearch', 'activeUsers', 'topUsers'];
    let result = [];

    if (!rows?.length) {
        return [];
    }

    for (let row of rows) {
        for (let type of types) {
            if (!row[type]?.length) {
                continue;
            }

            for (let item of row[type]) {
                const valueIndex = item.length - 1;
                const companyNameIndex = item.length - 2;
                const endElementsCount = isGroupedByOrg ? 2 : 1;
                const name = item.slice(0, -endElementsCount)
                    .map(getTitle)
                    .join(' ');

                result.push({
                    'Organisation': item[companyNameIndex],
                    'Name': name,
                    'Numbers': item[valueIndex].toLocaleString('en-GB'),
                    'Type': documentActionNames[type] ?? type,
                    'Period': simplifyDateRange(row.period),
                });
            }
        }
    }

    return result;
};

export const transformDataForOverallReport = (apiResponse) => {
    const rows = apiResponse.rows;

    if (!rows || !rows.length) {
        return [];
    }

    let result = [];

    for (let row of rows) {
        for (let key in row) {
            if (Object.prototype.hasOwnProperty.call(row[key], 'name')) { // Check if the current key is one of the categories we're interested in
                const item = row[key];

                result.push({
                    'name': item.name,
                    'numberOfActions': item.numberOfActions,
                    'hoursSaved': item.hoursSaved,
                    'costSaved': item.costSaved,
                    'period': apiResponse.period,
                });
            }
        }
    }

    return result;
};

export const datetimeToFormattedStringUtc = (date, emptyReplace = '-') => {
    if (!date) {
        return emptyReplace;
    }

    const dateWithoutTimezone = removeTimezoneOffset(new Date(date));

    return format(dateWithoutTimezone, datetimeFormat);
};

export const dateToFormattedStringUtc = (date, emptyReplace = '-') => {
    if (!date) {
        return emptyReplace;
    }

    const dateWithoutTimezone = removeTimezoneOffset(new Date(date));

    return format(dateWithoutTimezone, dateFormat);
};

export const datetimeToFormattedString = (date, emptyReplace = '-') => {
    return date ? format(new Date(date), datetimeFormat) : emptyReplace;
};

export const dateToFormattedString = (date, emptyReplace = '-') => {
    return date ? format(new Date(date), dateFormat) : emptyReplace;
};

export const extractDateFromIso = (iso) => {
    if (!iso) return iso;

    return iso.split('T')[0];
};

export const extractDateFromString = (dateString) => {
    const iso = new Date(dateString)
        .toISOString();

    return extractDateFromIso(iso);
};

export const addZeroTimeToIso = (iso) => {
    return `${extractDateFromString(iso)} 00:00:00`;
};

export const removeTimezoneOffset = (dateWithTimezone) => {
    return addMinutes(dateWithTimezone, dateWithTimezone.getTimezoneOffset());
};

export const toIsoIgnoreTimezone = (dateWithTimezone, isSecondsDisabled = false) => {
    const hours = dateWithTimezone.getHours()
        .toString()
        .padStart(2, '0');
    const minutes = dateWithTimezone.getMinutes()
        .toString()
        .padStart(2, '0');
    const seconds = dateWithTimezone.getSeconds()
        .toString()
        .padStart(2, '0');
    const milliseconds = dateWithTimezone.getMilliseconds()
        .toString()
        .padStart(3, '0');
    const year = dateWithTimezone.getFullYear()
        .toString()
        .padStart(2, '0');
    const month = (dateWithTimezone.getMonth() + 1)
        .toString()
        .padStart(2, '0');
    const day = dateWithTimezone.getDate()
        .toString()
        .padStart(2, '0');
    const date = `${year}-${month}-${day}`;

    return isSecondsDisabled
        ? `${date}T${hours}:${minutes}:00.000`                      // ISO: '2024-10-02T13:21:00.000'
        : `${date}T${hours}:${minutes}:${seconds}.${milliseconds}`; // ISO: '2024-10-02T13:21:21.378Z'
};

export const getDateOneMonthAgo = (startDate = new Date()) => {
    const currentDate = startDate;
    const currentDay = currentDate.getDate();
    const oneMonthAgo = new Date(currentDate);

    oneMonthAgo.setDate(1);
    oneMonthAgo.setMonth(currentDate.getMonth() - 1);

    const lastDayOfMonth = new Date(oneMonthAgo.getFullYear(), oneMonthAgo.getMonth() + 1, 0)
        .getDate();

    oneMonthAgo.setDate(Math.min(currentDay, lastDayOfMonth));

    return oneMonthAgo;
};

export const trimValues = (obj) => {
    const result = {};

    if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
        return obj;
    }

    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            if (typeof obj[key] === 'string') {
                result[key] = obj[key].trim();
            } else {
                result[key] = obj[key];
            }
        }
    }

    return result;
};

export const lowercaseObjectStrings = (obj, keys) => {
    const result = {};

    if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
        return obj;
    }

    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const value = obj[key];
            const shouldTransform = typeof value === 'string'
                && (!Array.isArray(keys) || keys.includes(key));

            result[key] = shouldTransform
                ? value.toLowerCase()
                : result[key] = value;
        }
    }

    return result;
};

export const camelCaseToText = (camelCase) => {
    if (!camelCase) return '';

    return camelCase.replace(/([A-Z])/g, ' $1');
};

export const formatNumberAsCurrency = (value, currencySymbol = '£') => {
    const number = typeof value === 'string'
        ? parseFloat(value.replace(/[^0-9.-]+/g, ''))
        : Number(value);

    if (isNaN(number)) {
        return value;
    }

    return `${currencySymbol}${number.toLocaleString('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};

export const parsePhoneNo = (string) => {
    if (!string) return null;

    const phoneNumber = parsePhoneNumberFromString(string, 'GB');

    return phoneNumber ? phoneNumber.number : null;
};

export const processPhoneNumber = (phoneNumber) => {
    const formattedPhone = parsePhoneNo(phoneNumber);

    return formattedPhone && isValidPhoneNumber(formattedPhone, 'GB') ? formattedPhone : null;
};
